"use client";

import { useState, FormEvent } from "react";
import { useRouter } from "next/navigation";

/**
 * VerifyCodeInput — lets the user type (or paste) a Ref code printed under
 * a signature and navigate to the full verification result page.
 *
 * Lightly cleans the input (trim + collapse internal whitespace) before
 * routing.  The backend normalises the code fully (strips dashes, uppercases,
 * re-adds the sf_ prefix), so we intentionally do NOT reimplement
 * normalize_ref here.
 */
export default function VerifyCodeInput() {
  const router = useRouter();
  const [value, setValue] = useState("");
  const [error, setError] = useState("");

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    // Light client-side clean: trim + collapse internal spaces
    const cleaned = value.trim().replace(/\s+/g, " ");
    if (!cleaned) {
      setError("Please enter a reference code.");
      return;
    }
    setError("");
    router.push(`/verify/${encodeURIComponent(cleaned)}`);
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
        Enter Ref code
      </p>
      <form onSubmit={handleSubmit} className="flex gap-2">
        <input
          type="text"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            if (error) setError("");
          }}
          placeholder="e.g. TMC1-CNC1-0ALZ-K2P9"
          className="flex-1 min-w-0 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 font-mono"
          spellCheck={false}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
        />
        <button
          type="submit"
          className="shrink-0 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1 transition-colors"
        >
          Verify
        </button>
      </form>
      {error && (
        <p className="mt-2 text-xs text-red-600">{error}</p>
      )}
      <p className="mt-3 text-xs text-slate-400 leading-relaxed">
        This is a quick online check — the full cryptographic proof travels
        inside the document itself and verifies offline.
      </p>
    </div>
  );
}
